import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { academyAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { PlayCircle, GraduationCap } from 'lucide-react';

/**
 * Bandeau d'avertissement Dashboard : alerte l'utilisateur dès qu'il lui reste
 * au moins un module Académie NON TERMINÉ (formation pas à jour), y compris une
 * nouvelle vidéo ajoutée à un module déjà terminé. L'emmène directement vers le(s)
 * module(s) concerné(s). Affiché pour tous les rôles SAUF les apprenants.
 */
const MAX_BUTTONS = 3;

export const AcademyUpdateBanner = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [mods, setMods] = useState([]);

    const isApprenant = user?.role === 'apprenant';

    useEffect(() => {
        if (isApprenant) return;
        let active = true;
        academyAPI.getModules()
            .then(res => {
                if (active) {
                    setMods((res.data || []).filter(m => !m.user_progress?.completed));
                }
            })
            .catch(() => {});
        return () => { active = false; };
    }, [isApprenant]);

    if (isApprenant || mods.length === 0) return null;

    const allNewVideos = mods.every(m => m.user_progress?.content_has_updates);
    const title = allNewVideos
        ? "🎬 Une nouvelle vidéo est disponible sur l'Académie"
        : "📚 Votre formation n'est pas à jour";
    const subtitle = allNewVideos
        ? "Merci de vous mettre à jour le plus rapidement possible."
        : `Il vous reste ${mods.length} module${mods.length > 1 ? 's' : ''} à terminer. Merci de vous mettre à jour le plus rapidement possible.`;

    const goToModule = (id) => navigate(`/academy/module/${id}`);
    const shown = mods.slice(0, MAX_BUTTONS);
    const extra = mods.length - shown.length;

    return (
        <div
            className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3.5"
            data-testid="dashboard-academy-update-banner"
        >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-amber-500 p-2 text-white shrink-0">
                        <PlayCircle className="h-5 w-5" />
                    </div>
                    <div>
                        <p className="font-semibold text-amber-900">{title}</p>
                        <p className="text-sm text-amber-800">{subtitle}</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 sm:justify-end shrink-0">
                    {shown.map(m => (
                        <Button
                            key={m.id}
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => goToModule(m.id)}
                            data-testid={`dashboard-academy-update-cta-${m.id}`}
                        >
                            <GraduationCap className="h-4 w-4 mr-2" />
                            {m.title}
                        </Button>
                    ))}
                    {extra > 0 && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-400 text-amber-800 hover:bg-amber-100"
                            onClick={() => navigate('/academy')}
                            data-testid="dashboard-academy-update-cta-more"
                        >
                            +{extra} autre{extra > 1 ? 's' : ''} — Voir l'Académie
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
